import { Injectable, Inject } from '@angular/core';
import { NetworkService } from '@angular-workspace/core';
import { FILE_BASE_URL } from '../controlplane/file-api.service';

import { RS_BASE_URL } from './dataplane.tokens';

/**
 * Resource Server API Service for handling NGSI-LD entity-related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class RsApiService {
  constructor(
    private networkService: NetworkService,
    @Inject(RS_BASE_URL) private baseRsPathUrl: string,
    @Inject(FILE_BASE_URL) private fileRsServer: string,
  ) {}

  /**
   * Get NGSI-LD entity by dataset ID
   * @param datasetId The ID of the dataset
   * @param page Page number (default: 1)
   * @param options Optional query parameters: size, time (ISO), endtime (ISO), timerel
   * @returns Promise with entity data
   */
  async entitiesNgsiLg(
    datasetId: string,
    page: number = 1,
    options?: { size?: number; time?: string; endtime?: string; timerel?: string },
  ): Promise<any> {
    try {
      const params = new URLSearchParams({ page: String(page) });
      if (options?.size != null) params.set('size', String(options.size));
      if (options?.time)    params.set('time', options.time);
      if (options?.endtime) params.set('endtime', options.endtime);
      if (options?.timerel) params.set('timerel', options.timerel);
      const url = `${this.baseRsPathUrl}ngsi-ld/v2/entities/${datasetId}?${params.toString()}`;
      const response = await this.networkService.get_api(url);
      return response;
    } catch (error) {
      console.error('Error fetching NGSI-LD entity:', error);
      throw error;
    }
  }

  /**
   * Download CSV file for a dataset
   * @param datasetId The ID of the dataset
   * @returns Promise with blob data for CSV download
   */
  async downloadCSV(datasetId: string): Promise<Blob> {
    try {
      // Construct the full URL: baseRsPathUrl/ngsi-ld/v2/<datasetId>/download
      const url = `${this.baseRsPathUrl}ngsi-ld/v2/${datasetId}/download`;
      
      // Make GET request with blob response type for file download
      const headers = { responseType: 'blob' };
      const response = await this.networkService.get_api(url, headers);
      return response;
    } catch (error) {
      console.error('Error downloading CSV:', error);
      throw error;
    }
  }

  /**
   * Download CSV file for a dataset using NGSI-LD endpoint from file server
   * @param datasetId The ID of the dataset
   * @returns Promise with blob data for CSV download
   */
  async downloadCSVNgslid(datasetId: string): Promise<Blob> {
    try {
      // Construct the full URL: fileRsServer/ngsi-ld/v2/<datasetId>/download
      const url = `${this.baseRsPathUrl}ngsi-ld/v2/${datasetId}/download`;
      
      // Make GET request with blob response type for file download
      const headers = { responseType: 'blob' };
      const response = await this.networkService.get_api(url, headers);
      return response;
    } catch (error) {
      console.error('Error downloading CSV via NGSI-LD:', error);
      throw error;
    }
  }
}
